import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { button, folder } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';
import type { SceneControls } from '../scene/controls/SceneControls';

interface DevMarker {
  id: string;
  name: string;
  mesh: THREE.Mesh;
}

type ChangeContext = { initial?: boolean };

const MARKER_COLOR = 0xff6600;
const MARKER_RADIUS = 0.02;
const DEBUG_RENDER_ORDER = 10;
const GIZMO_SIZE = 0.5;

const markerGeometry = new THREE.SphereGeometry(MARKER_RADIUS, 16, 16);
const markerMaterial = new THREE.MeshBasicMaterial({
  color: MARKER_COLOR,
  transparent: true,
  opacity: 0.9,
  depthWrite: false,
  depthTest: false,
});

const roundVec3 = (v: THREE.Vector3) => ({
  x: +v.x.toFixed(4),
  y: +v.y.toFixed(4),
  z: +v.z.toFixed(4),
});

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text).then(
    () => console.log(`[DevMarkers] ${label} copied to clipboard`),
    (err) => console.error(`[DevMarkers] Failed to copy ${label}`, err),
  );
};

interface UseDevMarkersOptions {
  scene: THREE.Scene | null;
  camera: THREE.PerspectiveCamera | null;
  controls: SceneControls | null;
  renderer: THREE.WebGLRenderer | null;
}

interface UseDevMarkersResult {
  buildSchema: () => Schema;
  schemaDeps: unknown[];
  captureSet: (set: unknown) => void;
}

export const useDevMarkers = ({
  scene,
  camera,
  controls,
  renderer,
}: UseDevMarkersOptions): UseDevMarkersResult => {
  const markersRef = useRef<DevMarker[]>([]);
  const markerCounterRef = useRef(0);
  const transformControlsRef = useRef<TransformControls | null>(null);
  const selectedMarkerIdRef = useRef<string | null>(null);
  const isProgrammaticRef = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markerSetRef = useRef<((values: Record<string, any>) => void) | null>(null);

  const [markerVersion, setMarkerVersion] = useState(0);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);

  const selectMarker = useCallback((id: string | null) => {
    const gizmo = transformControlsRef.current;
    if (!gizmo) return;

    if (id === null || id === selectedMarkerIdRef.current) {
      gizmo.detach();
      gizmo.getHelper().visible = false;
      selectedMarkerIdRef.current = null;
      setSelectedMarkerId(null);
      return;
    }

    const marker = markersRef.current.find((m) => m.id === id);
    if (!marker) return;

    gizmo.attach(marker.mesh);
    gizmo.getHelper().visible = true;
    selectedMarkerIdRef.current = id;
    setSelectedMarkerId(id);
  }, []);

  const removeMarker = useCallback((id: string) => {
    const idx = markersRef.current.findIndex((m) => m.id === id);
    if (idx === -1) return;
    const marker = markersRef.current[idx];

    if (selectedMarkerIdRef.current === id) {
      selectMarker(null);
    }

    scene?.remove(marker.mesh);
    marker.mesh.geometry.dispose();
    (marker.mesh.material as THREE.Material).dispose();
    markersRef.current.splice(idx, 1);
    setMarkerVersion((v) => v + 1);
  }, [scene, selectMarker]);

  const addMarker = useCallback(() => {
    if (!scene || !controls) return;

    markerCounterRef.current += 1;
    const id = `marker-${markerCounterRef.current}`;
    const name = `Marker ${markerCounterRef.current}`;

    const mesh = new THREE.Mesh(markerGeometry.clone(), markerMaterial.clone());
    mesh.position.copy(controls.orbit.target);
    mesh.renderOrder = DEBUG_RENDER_ORDER;
    mesh.name = id;
    scene.add(mesh);

    markersRef.current.push({ id, name, mesh });
    setMarkerVersion((v) => v + 1);

    requestAnimationFrame(() => selectMarker(id));
  }, [scene, controls, selectMarker]);

  const removeAllMarkers = useCallback(() => {
    for (const marker of [...markersRef.current]) {
      if (selectedMarkerIdRef.current === marker.id) {
        selectMarker(null);
      }
      scene?.remove(marker.mesh);
      marker.mesh.geometry.dispose();
      (marker.mesh.material as THREE.Material).dispose();
    }
    markersRef.current = [];
    setMarkerVersion((v) => v + 1);
  }, [scene, selectMarker]);

  // --- TransformControls setup ---
  useEffect(() => {
    if (!scene || !camera || !renderer || !controls) return;

    const gizmo = new TransformControls(camera, renderer.domElement);
    gizmo.setMode('translate');
    gizmo.setSize(GIZMO_SIZE);

    const helper = gizmo.getHelper();
    helper.visible = false;
    scene.add(helper);

    gizmo.addEventListener('dragging-changed', (event: { value: unknown }) => {
      controls.enabled = !(event.value as boolean);
    });

    gizmo.addEventListener('objectChange', () => {
      const obj = gizmo.object;
      if (!obj) return;
      const marker = markersRef.current.find((m) => m.mesh === obj);
      if (!marker) return;

      const setFn = markerSetRef.current;
      if (setFn) {
        isProgrammaticRef.current = true;
        setFn({
          [`${marker.id}_posX`]: +obj.position.x.toFixed(4),
          [`${marker.id}_posY`]: +obj.position.y.toFixed(4),
          [`${marker.id}_posZ`]: +obj.position.z.toFixed(4),
        });
        isProgrammaticRef.current = false;
      }
    });

    transformControlsRef.current = gizmo;

    return () => {
      gizmo.detach();
      scene.remove(helper);
      gizmo.dispose();
      transformControlsRef.current = null;
    };
  }, [scene, camera, renderer, controls]);

  // --- Cleanup markers on unmount ---
  useEffect(() => {
    return () => {
      for (const marker of markersRef.current) {
        scene?.remove(marker.mesh);
        marker.mesh.geometry.dispose();
        (marker.mesh.material as THREE.Material).dispose();
      }
      markersRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildSchema = useCallback((): Schema => {
    const schema: Schema = {};
    const markers = markersRef.current;

    schema['Add Marker'] = button(() => addMarker());

    if (markers.length > 0) {
      schema['Copy All'] = button(() => {
        const payload = markers.map((m) => ({
          name: m.name,
          position: roundVec3(m.mesh.position),
        }));
        copyToClipboard(JSON.stringify(payload, null, 2), 'All markers');
      });

      schema['Remove All'] = button(() => removeAllMarkers());
    }

    for (const marker of markers) {
      const isSelected = selectedMarkerId === marker.id;
      const markerFolder: Schema = {};

      const selectLabel = isSelected ? 'Deselect Gizmo' : 'Select Gizmo';
      markerFolder[`${selectLabel}##${marker.id}`] = button(
        () => selectMarker(isSelected ? null : marker.id),
      );

      markerFolder[`${marker.id}_posX`] = {
        label: 'X',
        value: +marker.mesh.position.x.toFixed(4),
        step: 0.1,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          marker.mesh.position.x = v;
        },
      };
      markerFolder[`${marker.id}_posY`] = {
        label: 'Y',
        value: +marker.mesh.position.y.toFixed(4),
        step: 0.1,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          marker.mesh.position.y = v;
        },
      };
      markerFolder[`${marker.id}_posZ`] = {
        label: 'Z',
        value: +marker.mesh.position.z.toFixed(4),
        step: 0.1,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          marker.mesh.position.z = v;
        },
      };

      markerFolder[`Copy Position##${marker.id}`] = button(() => {
        const pos = roundVec3(marker.mesh.position);
        copyToClipboard(JSON.stringify(pos), `Marker "${marker.name}"`);
      });

      markerFolder[`Remove##${marker.id}`] = button(
        () => removeMarker(marker.id),
      );

      const title = isSelected ? `● ${marker.name}` : marker.name;
      schema[title] = folder(markerFolder, { collapsed: !isSelected });
    }

    return schema;
  }, [markerVersion, selectedMarkerId, addMarker, removeMarker, removeAllMarkers, selectMarker]);

  const captureSet = useCallback((set: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    markerSetRef.current = set as any;
  }, []);

  return {
    buildSchema,
    schemaDeps: [buildSchema],
    captureSet,
  };
};
